import { useCallback, useEffect, useRef, useState } from 'react'

type TypewriterOptions = {
  minDelayMs?: number
  maxDelayMs?: number
  minCharsPerTick?: number
  maxCharsPerTick?: number
  punctuationPauseMs?: number
  newlinePauseMs?: number
  resetOnShrink?: boolean
  enabled?: boolean
  initialDelayMs?: number
}

type ResolvedOptions = Required<TypewriterOptions>

const randInt = (min: number, max: number) => Math.floor(min + Math.random() * (max - min + 1))
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))

const URL_REGEX = /\b(?:https?:\/\/|www\.)[^\s<>()"']+/i
const MD_LINK_REGEX = /\[[^\]\n]+\]\((?:\s*)[^)\s]+(?:\s*)\)/
const PUNCTUATION_CHARS = new Set(['.', '!', '?'])

function findFirstInstantToken(s: string): { index: number; text: string } | null {
  const md = MD_LINK_REGEX.exec(s)
  const url = URL_REGEX.exec(s)

  if (!md && !url) return null

  const mdIndex = md?.index ?? Infinity
  const urlIndex = url?.index ?? Infinity

  return mdIndex <= urlIndex ? { index: md!.index!, text: md![0] } : { index: url!.index!, text: url![0] }
}

export function useGRTypewriter(fullText: string, opts: TypewriterOptions = {}): { text: string; isFinished: boolean; isTyping: boolean; isDelaying: boolean } {
  const {
    minDelayMs = 20,
    maxDelayMs = 50,
    minCharsPerTick = 1,
    maxCharsPerTick = 3,
    punctuationPauseMs = 200,
    newlinePauseMs = 300,
    resetOnShrink = true,
    enabled = true,
    initialDelayMs = 0,
  } = opts

  const [typed, setTyped] = useState('')
  const [isRunning, setIsRunning] = useState(true)
  const [isDelaying, setIsDelaying] = useState(false)

  const consumedLenRef = useRef(0)
  const bufferRef = useRef('')
  const timeoutRef = useRef<number | null>(null)
  const initialDelayAppliedRef = useRef(false)
  const fullTextRef = useRef(fullText)
  const optsRef = useRef<ResolvedOptions>({
    minDelayMs,
    maxDelayMs,
    minCharsPerTick,
    maxCharsPerTick,
    punctuationPauseMs,
    newlinePauseMs,
    resetOnShrink,
    enabled,
    initialDelayMs,
  })

  const clearTimer = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Keep refs in sync with latest props
  useEffect(() => {
    fullTextRef.current = fullText
    optsRef.current = {
      minDelayMs,
      maxDelayMs,
      minCharsPerTick,
      maxCharsPerTick,
      punctuationPauseMs,
      newlinePauseMs,
      resetOnShrink,
      enabled,
      initialDelayMs,
    }
  }, [fullText, minDelayMs, maxDelayMs, minCharsPerTick, maxCharsPerTick, punctuationPauseMs, newlinePauseMs, resetOnShrink, enabled, initialDelayMs])

  const resetInternal = useCallback(() => {
    clearTimer()
    consumedLenRef.current = 0
    bufferRef.current = fullTextRef.current
    setTyped('')
    setIsRunning(true)
    setIsDelaying(false)
    initialDelayAppliedRef.current = false
  }, [clearTimer])

  useEffect(() => {
    if (!enabled) {
      clearTimer()
      consumedLenRef.current = fullText.length
      bufferRef.current = ''
      setTyped(fullText)
      setIsRunning(false)
      return
    }

    if (fullText.length < consumedLenRef.current) {
      if (resetOnShrink) {
        resetInternal()
      } else {
        consumedLenRef.current = fullText.length
        bufferRef.current = ''
        setTyped((prev) => prev.slice(0, fullText.length))
      }
      return
    }

    const newPortion = fullText.slice(consumedLenRef.current)
    if (newPortion) {
      bufferRef.current = newPortion
      setIsRunning(true)
      initialDelayAppliedRef.current = false
    }
  }, [fullText, enabled, resetOnShrink, clearTimer, resetInternal])

  const tick = useCallback(() => {
    const o = optsRef.current

    if (!o.enabled) return

    const buf = bufferRef.current
    if (!buf) {
      setIsRunning(false)
      return
    }

    const minC = clamp(o.minCharsPerTick, 1, 50)
    const maxC = clamp(o.maxCharsPerTick, minC, 50)
    const charsThisTick = randInt(minC, maxC)

    const instant = findFirstInstantToken(buf)

    let reveal: string
    let pauseCheckChar: string

    if (instant && instant.index < charsThisTick) {
      const prefix = buf.slice(0, instant.index)
      reveal = prefix + instant.text
      pauseCheckChar = prefix.slice(-1)
    } else {
      reveal = buf.slice(0, charsThisTick)
      pauseCheckChar = reveal.slice(-1)
    }

    bufferRef.current = buf.slice(reveal.length)
    consumedLenRef.current += reveal.length

    let extraPause = 0
    if (pauseCheckChar === '\n') extraPause += o.newlinePauseMs
    if (PUNCTUATION_CHARS.has(pauseCheckChar)) extraPause += o.punctuationPauseMs

    if (reveal) setTyped((prev) => prev + reveal)

    timeoutRef.current = window.setTimeout(tick, randInt(o.minDelayMs, o.maxDelayMs) + extraPause)
  }, [])

  useEffect(() => {
    const o = optsRef.current
    if (!o.enabled || !isRunning) return

    if (!initialDelayAppliedRef.current && o.initialDelayMs > 0) {
      initialDelayAppliedRef.current = true
      setIsDelaying(true)
      timeoutRef.current = window.setTimeout(() => {
        setIsDelaying(false)
        tick()
      }, o.initialDelayMs)
    } else {
      tick()
    }

    return clearTimer
  }, [isRunning, tick, clearTimer])

  const isFinished = enabled ? !isRunning && bufferRef.current.length === 0 && consumedLenRef.current >= fullText.length : true
  const isTyping = enabled ? isRunning && !isFinished && !isDelaying : false

  return { text: typed, isFinished, isTyping, isDelaying }
}
